import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchFlows } from "../api/client";
import type { FlowSummary } from "../api/types";
import { useTenantConfig } from "../hooks/useTenantConfig";
import { useAuth } from "../auth/useAuth";
import { canUseConversazioni } from "../auth/permissions";

export function FlowList() {
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { config: tenantConfig } = useTenantConfig();
  const { user } = useAuth();

  useEffect(() => {
    fetchFlows()
      .then((data) => setFlows(data.flows))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Una sola pagina di configurazione per tenant: se il modulo "conversazioni"
  // è abilitato, l'Agente Conversazionale compare come voce in più qui invece
  // di vivere in un'isola separata (era /conversazioni/settings).
  const showAgente = (tenantConfig?.modules ?? []).includes("conversazioni") && canUseConversazioni(user?.roles ?? []);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-center">
          <div className="spinner spinner-lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="card" style={{ padding: "var(--space-6)", background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ verticalAlign: "middle", marginRight: "var(--space-2)" }}>error</span>
          Errore caricamento flussi: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title">
        <span className="material-symbols-outlined" style={{ fontSize: 28, verticalAlign: "middle", marginRight: "var(--space-2)" }}>tune</span>
        Configurazione
      </h1>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "var(--space-4) var(--space-6)", borderBottom: "1px solid var(--border-color)", background: "var(--indaco-gray-50)" }}>
          <h2 style={{ margin: 0, fontSize: "var(--font-size-md)", fontWeight: "var(--font-weight-semibold)" }}>
            Flussi attivi
          </h2>
        </div>

        {flows.length === 0 && !showAgente ? (
          <div className="text-center text-muted" style={{ padding: "var(--space-10)" }}>
            Nessun flusso configurato per questo tenant
          </div>
        ) : (
          <div>
            {flows.map((flow) => (
              <button
                key={flow.flow_id}
                className="flow-list-item"
                onClick={() => navigate(`/settings/${flow.flow_id}`)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flex: 1, minWidth: 0 }}>
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: 20,
                      color: flow.enabled ? "var(--indaco-success)" : "var(--indaco-gray-500)",
                    }}
                  >
                    {flow.enabled ? "check_circle" : "pause_circle"}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-base)" }}>
                      {flow.label}
                    </div>
                    {flow.mailboxes.length > 0 && (
                      <div style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginTop: 2 }}>
                        {flow.mailboxes.join(", ")}
                      </div>
                    )}
                  </div>
                </div>
                <span className="material-symbols-outlined" style={{ color: "var(--indaco-gray-500)", fontSize: 20 }}>
                  chevron_right
                </span>
              </button>
            ))}
            {showAgente && (
              <button
                key="agente-conversazionale"
                className="flow-list-item"
                onClick={() => navigate("/settings/agente")}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flex: 1, minWidth: 0 }}>
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 20, color: "var(--indaco-brand-primary)" }}
                  >
                    smart_toy
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-base)" }}>
                      Agente Conversazionale
                    </div>
                    <div style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", marginTop: 2 }}>
                      Requisiti, ciclo di vita sessione, disambiguazione
                    </div>
                  </div>
                </div>
                <span className="material-symbols-outlined" style={{ color: "var(--indaco-gray-500)", fontSize: 20 }}>
                  chevron_right
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
